/**
 * VoteBreakdownChart Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 7: Create VoteBreakdownChart component
 *
 * Horizontal stacked bar chart showing vote distribution.
 * No external charting library - pure CSS implementation.
 *
 * @see AC-4.2.7.6 - Chart updates when new vote is cast
 *
 * @module components/governance/VoteBreakdownChart
 */

// =============================================================================
// Types
// =============================================================================

export interface VoteBreakdownChartProps {
  /** Yes vote count */
  yes: number;
  /** No vote count */
  no: number;
  /** Abstain vote count */
  abstain: number;
  /** Needs Refinement vote count */
  needsRefinement: number;
  /** Show legend */
  showLegend?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const COLORS = {
  yes: 'bg-green-500',
  no: 'bg-red-500',
  abstain: 'bg-gray-400',
  needsRefinement: 'bg-yellow-500',
};

const LABELS = {
  yes: 'Yes',
  no: 'No',
  abstain: 'Abstain',
  needsRefinement: 'Needs Refinement',
};

// =============================================================================
// Component
// =============================================================================

/**
 * Horizontal stacked bar chart for vote breakdown
 */
export function VoteBreakdownChart({
  yes,
  no,
  abstain,
  needsRefinement,
  showLegend = true,
}: VoteBreakdownChartProps) {
  const total = yes + no + abstain + needsRefinement;

  // Calculate percentages
  const getPercentage = (count: number) => {
    if (total === 0) return 0;
    return (count / total) * 100;
  };

  const segments = [
    { key: 'yes', count: yes, color: COLORS.yes, label: LABELS.yes },
    { key: 'no', count: no, color: COLORS.no, label: LABELS.no },
    { key: 'abstain', count: abstain, color: COLORS.abstain, label: LABELS.abstain },
    { key: 'needsRefinement', count: needsRefinement, color: COLORS.needsRefinement, label: LABELS.needsRefinement },
  ];

  return (
    <div className="space-y-3" data-testid="vote-breakdown-chart">
      {/* Stacked bar */}
      <div
        className="h-6 flex rounded-lg overflow-hidden bg-muted"
        data-testid="vote-breakdown-bar"
        role="img"
        aria-label={`Vote breakdown: ${yes} yes, ${no} no, ${abstain} abstain, ${needsRefinement} needs refinement`}
      >
        {segments.map(({ key, count, color }) => {
          const percentage = getPercentage(count);
          if (count === 0) return null;

          // Ensure minimum visible width for very small segments (at least 2%)
          const displayWidth = percentage > 0 && percentage < 2 ? 2 : percentage;

          return (
            <div
              key={key}
              className={`${color} transition-all duration-300`}
              style={{ width: `${displayWidth}%`, minWidth: count > 0 ? '4px' : undefined }}
              data-testid={`vote-breakdown-segment-${key}`}
              title={`${LABELS[key as keyof typeof LABELS]}: ${count} (${percentage.toFixed(1)}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap gap-4 text-sm" data-testid="vote-breakdown-legend">
          {segments.map(({ key, count, color, label }) => (
            <div key={key} className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded ${color}`}
                data-testid={`vote-legend-color-${key}`}
              />
              <span className="text-muted-foreground">
                {label}:{' '}
                <span
                  className="font-medium text-foreground"
                  data-testid={`vote-legend-count-${key}`}
                >
                  {count}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Total */}
      <div className="text-xs text-muted-foreground" data-testid="vote-breakdown-total">
        Total votes: {total}
      </div>
    </div>
  );
}

export default VoteBreakdownChart;
